import { useMutation } from "@tanstack/react-query";
import { Radio, Text } from "@ui-kitten/components";
import Button from "components/Button";
import Container from "components/Container";
import Dialog from "components/Dialog";
import LoginOrSignupForm from "components/LoginOrSignupForm";
import Spacer from "components/Spacer";
import StripePaymentForm, {
  StripeTokenError,
  StripeTokenResponse,
  TokenReceivedOutcome,
} from "components/StripePaymentForm/StripePaymentForm";
import { setEmail } from "features/slices/loginFlowSlice";
import {
  BillingInterval,
  SeatDetails,
  SectionDetails,
  TicketCartDetails,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import useAuth from "hooks/useAuth";
import useCurrentUser from "hooks/useCurrentUser";
import { ShopProductV3 } from "lib/sanity.types";
import { updateBillingFetcher } from "queries/account.queries";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import {
  fetchPlanPackageAvailableSections,
  holdSeasonTicketSeatsRequest,
  useFetchPlanPackageAvailableSectionsQuery,
  useFetchPlanPackageSectionAvailableBlocksQuery,
} from "queries/planPackage.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import {
  addParkingPackageSeatsRequest,
  calculateSeasonTicketSeatChargeRequest,
  confirmSeasonTicketPurchaseRequest,
  deleteTicketCartFetcher,
} from "queries/ticket.queries";
import { useFetchSeatsBySectionQuery } from "queries/venue.queries";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pressable, ScrollView, View } from "react-native";
import VerifyPhonePopup from "screens/Dashboard/components/VerifyPhonePopup";
import SeatQuantitySelector from "screens/SeatSelection/components/SeatQuantitySelector";
import VenueSeatMap from "screens/SeatSelection/components/VenueSeatMap";
import AvailableSeatDots from "screens/ShopPhaseTwo/components/AvailableSeatDots";
import { formatCurrency } from "utils/appUtils";
import toast from "utils/toast";
import { PurchaseSummary } from "./CheckoutV3";
import CardOnFileV3 from "./components/CardOnFileV3";
import SeasonOrderSummaryV3 from "./components/SeasonOrderSummaryV3";
import { defaultVariant, LINE, LiveVariant, PAPER } from "./helpers";

type TdcSeatSelection = { section: string; row: string; seat: string };

const InlineError = ({
  message,
  actionLabel,
  onAction,
}: {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}) => (
  <View className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
    <View className="flex-row items-center justify-between gap-3">
      <Text category="c1" className="flex-1">
        {message}
      </Text>
      {actionLabel && onAction ? (
        <Button appearance="ghost" size="tiny" onPress={onAction}>
          {actionLabel}
        </Button>
      ) : null}
    </View>
  </View>
);

interface Props {
  product: ShopProductV3;
  variants: LiveVariant[];
  initialVariantKey?: string;
  // Email captured at the shop's email gate; prefills the login/signup
  // forms shown when reserving seats requires an account.
  initialEmail?: string;
  primaryColor: string;
  onBack: () => void;
  onComplete: (summary: PurchaseSummary) => void;
}

// V3-native season-ticket checkout: pick a plan variant, select exact
// seats on the venue map, hold them, add TDC parking when configured, and
// pay — all with local state (no phase-two redux) and the V3 look. The
// seat/hold/charge/confirm endpoints and leaf components (seat map, seat
// dots, order summary) are shared with the legacy flow; the orchestration
// is deliberately duplicated per product decision.
const SeasonCheckoutV3 = ({
  product,
  variants,
  initialVariantKey,
  initialEmail,
  primaryColor,
  onBack,
  onComplete,
}: Props) => {
  const auth = useAuth();
  const dispatch = useAppDispatch();

  // Seed the loginFlow slice so both LoginForm (username) and SignupForm →
  // NewMemberForm (email) open prefilled with the gate-captured address.
  useEffect(() => {
    if (initialEmail) dispatch(setEmail(initialEmail));
  }, [dispatch, initialEmail]);
  const { account, fetchAccount } = useAccount();
  const { currentUser } = useCurrentUser();
  const { data: branding } = useFetchTenantBrandingQuery();
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const { tenantThemeColor } = useAppSelector((state) => state.tenant);

  const [selectedVariantKey, setSelectedVariantKey] = useState(
    initialVariantKey,
  );
  const selected =
    variants.find(({ variant }) => variant.key === selectedVariantKey) ??
    defaultVariant(variants);
  const { planPackage, variant } = selected;
  const tdc = variant.tdc ?? {};
  const isTdcPlan = Boolean(tdc.packageListId && tdc.packageId);
  const isParkingRequired = Boolean(tdc.parkingPackageListId);

  const eventType = product.seasonEventType;
  const venueName = product.seasonVenueName;
  const canRenderSeatMap =
    !!branding?.type && !!branding?.subdomain && !!eventType && !!venueName;

  const [billingInterval, setBillingInterval] = useState<BillingInterval>(
    planPackage.billingIntervals.includes("MONTHLY")
      ? "MONTHLY"
      : "FULL_SEASON",
  );

  // Seat selection state.
  const [selectedSection, setSelectedSection] = useState<string | undefined>();
  const [venueMapKey, setVenueMapKey] = useState(0);
  const [selectedSeats, setSelectedSeats] = useState<SeatDetails[]>([]);
  const [cartSeats, setCartSeats] = useState<SeatDetails[]>([]);
  const [holdExpiresAt, setHoldExpiresAt] = useState<Date | null>(null);
  const [holdRemainingMs, setHoldRemainingMs] = useState<number | null>(null);
  const [tdcCartId, setTdcCartId] = useState<string | null>(null);
  const [previewCharge, setPreviewCharge] = useState<TicketCartDetails | null>(
    null,
  );
  const [finalCharge, setFinalCharge] = useState<TicketCartDetails | null>(
    null,
  );

  // Parking (TDC-configured plans only).
  const [parkingDialogVisible, setParkingDialogVisible] = useState(false);
  const [parkingSections, setParkingSections] = useState<SectionDetails[]>([]);
  const [isFetchingParkingSections, setIsFetchingParkingSections] =
    useState(false);
  const [selectedParkingSection, setSelectedParkingSection] = useState<
    string | undefined
  >();
  const [parkingQuantity, setParkingQuantity] = useState(1);
  const [confirmedParkingSelections, setConfirmedParkingSelections] = useState<
    TdcSeatSelection[]
  >([]);
  const [confirmedParkingSectionLabel, setConfirmedParkingSectionLabel] =
    useState<string | undefined>();

  // Deferred actions and dialogs.
  const [loginVisible, setLoginVisible] = useState(false);
  const [verifyPhoneVisible, setVerifyPhoneVisible] = useState(false);
  const [pendingHoldSeats, setPendingHoldSeats] = useState<
    SeatDetails[] | null
  >(null);
  const [pendingConfirm, setPendingConfirm] = useState(false);

  // Error banners.
  const [holdError, setHoldError] = useState<string | null>(null);
  const [chargeError, setChargeError] = useState<string | null>(null);
  const [parkingSectionsError, setParkingSectionsError] = useState<
    string | null
  >(null);
  const [parkingSubmitError, setParkingSubmitError] = useState<string | null>(
    null,
  );
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  const normalizedSection = useMemo(
    () => selectedSection?.split(",")[0]?.trim(),
    [selectedSection],
  );

  // ---- Data ----
  const {
    data: sectionsRaw,
    isFetching: isFetchingSections,
    isError: isSectionsError,
    refetch: refetchSections,
  } = useFetchPlanPackageAvailableSectionsQuery(
    planPackage.id,
    tdc.packageListId,
    tdc.packageId,
  );
  const sections = useMemo(() => sectionsRaw || [], [sectionsRaw]);

  const {
    data: blocksRaw,
    isFetching: isFetchingBlocks,
    isError: isBlocksError,
    refetch: refetchBlocks,
  } = useFetchPlanPackageSectionAvailableBlocksQuery(
    planPackage.id,
    normalizedSection,
    eventType,
    tdc.packageListId,
    tdc.packageId,
  );
  const blocks = useMemo(() => blocksRaw || [], [blocksRaw]);

  const availableSeatIds = useMemo(() => {
    const ids = new Set<number>();
    for (const block of blocks) {
      for (const ticket of block.tickets) {
        if (ticket.seatId) ids.add(ticket.seatId);
      }
    }
    return ids;
  }, [blocks]);

  const seatRowContext = useMemo(() => {
    const seatGroups = planPackage.primaryComponent?.seatGroups || [];
    const matching =
      seatGroups.find(
        (sg) => sg.venueName === venueName && sg.eventType === eventType,
      ) || seatGroups[0];
    if (!matching || !venueName || !eventType) return undefined;
    return { venueId: matching.venueId, eventTypeName: eventType };
  }, [eventType, planPackage.primaryComponent?.seatGroups, venueName]);

  const { data: seatsByRow, isFetching: isFetchingSeatsByRow } =
    useFetchSeatsBySectionQuery(
      seatRowContext?.venueId,
      normalizedSection,
      seatRowContext?.eventTypeName,
    );

  const {
    data: parkingBlocksRaw,
    isFetching: isFetchingParkingBlocks,
    isError: isParkingBlocksError,
    refetch: refetchParkingBlocks,
  } = useFetchPlanPackageSectionAvailableBlocksQuery(
    planPackage.id,
    selectedParkingSection,
    "PARKING",
    tdc.parkingPackageListId,
    tdc.packageId,
  );
  const parkingBlocks = useMemo(
    () => parkingBlocksRaw || [],
    [parkingBlocksRaw],
  );
  const maxParkingQuantity = useMemo(() => {
    const count = parkingBlocks.reduce(
      (total, block) => total + (block.tickets?.length || 0),
      0,
    );
    return Math.min(count, 20);
  }, [parkingBlocks]);

  // ---- Mutations ----
  const { mutateAsync: holdSeats, isLoading: isHolding } = useMutation(
    holdSeasonTicketSeatsRequest,
  );
  const { mutateAsync: calculateCharge, isLoading: isCalculatingCharge } =
    useMutation(calculateSeasonTicketSeatChargeRequest);
  const { mutateAsync: previewChargeRequest, isLoading: isPreviewingCharge } =
    useMutation(calculateSeasonTicketSeatChargeRequest);
  const { mutateAsync: confirmPurchase, isLoading: isConfirming } = useMutation(
    confirmSeasonTicketPurchaseRequest,
  );
  const { mutateAsync: deleteCart, isLoading: isDeletingCart } = useMutation(
    deleteTicketCartFetcher,
  );
  const { mutateAsync: addParkingSeats, isLoading: isAddingParking } =
    useMutation(addParkingPackageSeatsRequest);
  const { mutateAsync: updateBilling, isLoading: isUpdatingBilling } =
    useMutation(updateBillingFetcher);

  // ---- Derived ----
  const summarySeats = cartSeats.length > 0 ? cartSeats : selectedSeats;
  const summaryCharge = cartSeats.length > 0 ? finalCharge : previewCharge;
  const isSummaryLoading =
    cartSeats.length > 0 ? isCalculatingCharge : isPreviewingCharge;
  const isCartActive = cartSeats.length > 0;
  const hasRequiredParking =
    !isParkingRequired || confirmedParkingSelections.length > 0;
  const hasPaymentMethodOnFile = !!account?.billingLast4;
  const showPayment = isCartActive && auth.isLoggedIn && hasRequiredParking;
  const chargeTotal = useMemo(() => {
    if (!finalCharge) return 0;
    // ?? not ||: a totalCreditCardCharges of "0" is a real card charge of $0
    // (e.g. fully covered by account credit) and must not fall back to the
    // order's full price.
    const raw = finalCharge.totalCreditCardCharges ?? finalCharge.totalPrice;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [finalCharge]);

  const seatIdsOf = (seats: SeatDetails[]) =>
    seats.map((s) => s.id).filter((id): id is number => typeof id === "number");

  const seatSelectionsOf = (seats: SeatDetails[]): TdcSeatSelection[] =>
    isTdcPlan
      ? seats.map((s) => ({ section: s.section, row: s.row, seat: s.seat }))
      : [];

  const buildTdcPackageSelections = (
    seatSelections: TdcSeatSelection[],
    parkingSelections: TdcSeatSelection[],
  ) => {
    const selections: Array<{
      tdcPackageListId: string;
      tdcPackageId: string;
      selections: TdcSeatSelection[];
    }> = [];
    if (tdc.packageListId && tdc.packageId && seatSelections.length > 0) {
      selections.push({
        tdcPackageListId: tdc.packageListId,
        tdcPackageId: tdc.packageId,
        selections: seatSelections,
      });
    }
    if (
      tdc.parkingPackageListId &&
      tdc.packageId &&
      parkingSelections.length > 0
    ) {
      selections.push({
        tdcPackageListId: tdc.parkingPackageListId,
        tdcPackageId: tdc.packageId,
        selections: parkingSelections,
      });
    }
    return selections;
  };

  // ---- Reset helpers ----
  const chargePreviewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const chargePreviewRequestIdRef = useRef(0);

  const clearSeatAndCartState = () => {
    if (chargePreviewDebounceRef.current) {
      clearTimeout(chargePreviewDebounceRef.current);
    }
    chargePreviewRequestIdRef.current++;
    setSelectedSeats([]);
    setCartSeats([]);
    setHoldExpiresAt(null);
    setHoldRemainingMs(null);
    setTdcCartId(null);
    setPreviewCharge(null);
    setFinalCharge(null);
    setPendingConfirm(false);
    setParkingSections([]);
    setSelectedParkingSection(undefined);
    setParkingDialogVisible(false);
    setParkingQuantity(1);
    setConfirmedParkingSelections([]);
    setConfirmedParkingSectionLabel(undefined);
    setHoldError(null);
    setChargeError(null);
    setParkingSectionsError(null);
    setParkingSubmitError(null);
    setPurchaseError(null);
  };

  const handleSectionChanged = (section?: string) => {
    setSelectedSection(section);
    clearSeatAndCartState();
  };

  const handleBackToSections = () => {
    setSelectedSection(undefined);
    setVenueMapKey((k) => k + 1);
    clearSeatAndCartState();
  };

  const handleVariantChange = async (next: LiveVariant) => {
    if (next.variant.key === selected.variant.key) return;
    setLoginVisible(false);
    setVerifyPhoneVisible(false);
    setPendingHoldSeats(null);
    try {
      if (isCartActive || tdcCartId) await deleteCart();
    } catch {
      // The global HTTP interceptor already toasts the localized message.
    } finally {
      setSelectedSection(undefined);
      setVenueMapKey((k) => k + 1);
      clearSeatAndCartState();
    }
    setSelectedVariantKey(next.variant.key);
  };

  // Billing interval must be one the active plan offers.
  useEffect(() => {
    setBillingInterval((current) =>
      planPackage.billingIntervals.includes(current)
        ? current
        : planPackage.billingIntervals.includes("MONTHLY")
          ? "MONTHLY"
          : "FULL_SEASON",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planPackage.id]);

  // ---- Charge preview (debounced) for seats selected but not yet held ----
  useEffect(() => {
    if (isCartActive || pendingHoldSeats || isHolding) return;
    const seatIds = seatIdsOf(selectedSeats).sort((a, b) => a - b);
    if (!normalizedSection || !eventType || seatIds.length === 0) {
      setPreviewCharge(null);
      return;
    }
    if (chargePreviewDebounceRef.current) {
      clearTimeout(chargePreviewDebounceRef.current);
    }
    const requestId = ++chargePreviewRequestIdRef.current;
    chargePreviewDebounceRef.current = setTimeout(() => {
      const tdcPackageSelections = buildTdcPackageSelections(
        seatSelectionsOf(selectedSeats),
        confirmedParkingSelections,
      );
      previewChargeRequest({
        planPackageId: planPackage.id,
        section: normalizedSection,
        seatIds,
        eventType,
        tdcPackageBuyerTypeId: tdc.packageBuyerTypeId || undefined,
        tdcPackageSelections:
          tdcPackageSelections.length > 0 ? tdcPackageSelections : undefined,
      })
        .then((charge) => {
          if (chargePreviewRequestIdRef.current !== requestId) return;
          setPreviewCharge(charge);
        })
        .catch(() => {
          if (chargePreviewRequestIdRef.current !== requestId) return;
          setPreviewCharge(null);
        });
    }, 250);
    return () => {
      if (chargePreviewDebounceRef.current) {
        clearTimeout(chargePreviewDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    confirmedParkingSelections,
    eventType,
    isCartActive,
    isHolding,
    normalizedSection,
    pendingHoldSeats,
    planPackage.id,
    selectedSeats,
  ]);

  // ---- Hold ----
  const holdSelectedSeats = async (seats: SeatDetails[]) => {
    const seatIds = seatIdsOf(seats);
    if (!seats.length || !seatIds.length) return;
    setTdcCartId(null);
    setHoldError(null);
    setChargeError(null);
    setPurchaseError(null);
    try {
      const holdResponse = await holdSeats({
        planPackageId: planPackage.id,
        section: seats[0].section,
        seatIds,
        eventType,
        tdcPackageListId: tdc.packageListId,
        tdcPackageBuyerTypeId: tdc.packageBuyerTypeId,
        tdcPackageId: tdc.packageId,
      });
      setHoldExpiresAt(
        holdResponse.expiresAt ? new Date(holdResponse.expiresAt) : null,
      );
      setTdcCartId(holdResponse.tdcCart?.cartId || null);
      setConfirmedParkingSelections([]);
      setConfirmedParkingSectionLabel(undefined);
      if (isParkingRequired) await openParkingDialog(true);
    } catch {
      // Inline banner only; the HTTP interceptor already toasts the
      // localized message (e.g. TICKET_ALREADY_RESERVED).
      setHoldError("Couldn't reserve seats. Please try again.");
      return;
    }
    setCartSeats(seats);
    setFinalCharge(previewCharge);
    setPreviewCharge(null);
  };

  const handleReserve = async () => {
    if (!normalizedSection) {
      toast.error("Select a section first.");
      return;
    }
    if (selectedSeats.length === 0) return;
    if (!auth.isLoggedIn) {
      setPendingHoldSeats(selectedSeats);
      setLoginVisible(true);
      return;
    }
    await holdSelectedSeats(selectedSeats);
  };

  // Held-seat reservation deferred until the shopper signs in.
  useEffect(() => {
    if (!pendingHoldSeats || !auth.isLoggedIn) return;
    setLoginVisible(false);
    holdSelectedSeats(pendingHoldSeats)
      .catch(() => undefined)
      .finally(() => setPendingHoldSeats(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isLoggedIn, pendingHoldSeats]);

  const handleReleaseTickets = async () => {
    try {
      await deleteCart();
      clearSeatAndCartState();
    } catch {
      // The global HTTP interceptor already toasts the localized message.
    }
  };

  // ---- Hold countdown ----
  useEffect(() => {
    if (!holdExpiresAt) {
      setHoldRemainingMs(null);
      return;
    }
    const tick = () => setHoldRemainingMs(holdExpiresAt.getTime() - Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [holdExpiresAt]);

  const holdCountdownText = useMemo(() => {
    if (!holdExpiresAt || holdRemainingMs === null) return null;
    const totalSeconds = Math.max(0, Math.floor(holdRemainingMs / 1000));
    if (totalSeconds <= 0) return "Hold expired. Please reserve again.";
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `Seats held — expires in ${minutes}:${String(seconds).padStart(2, "0")}`;
  }, [holdExpiresAt, holdRemainingMs]);

  // ---- Parking ----
  const openParkingDialog = async (resetSelection: boolean) => {
    if (!tdc.parkingPackageListId || !tdc.packageId) return;
    setIsFetchingParkingSections(true);
    setParkingDialogVisible(true);
    setParkingSections([]);
    setParkingSectionsError(null);
    setParkingSubmitError(null);
    if (resetSelection) {
      setSelectedParkingSection(undefined);
      setParkingQuantity(1);
    }
    try {
      const result = await fetchPlanPackageAvailableSections(planPackage.id, {
        tdcPackageListId: tdc.parkingPackageListId,
        packageId: tdc.packageId,
      });
      setParkingSections(result || []);
    } catch {
      setParkingSectionsError("Couldn't load parking sections. Try again.");
    } finally {
      setIsFetchingParkingSections(false);
    }
  };

  useEffect(() => {
    if (maxParkingQuantity === 0) return;
    if (parkingQuantity <= maxParkingQuantity) return;
    setParkingQuantity(maxParkingQuantity);
  }, [maxParkingQuantity, parkingQuantity]);

  const handleSubmitParking = async () => {
    if (!selectedParkingSection) {
      toast.error("Select a parking section first.");
      return;
    }
    if (!tdc.parkingPackageListId || !tdc.parkingPackageBuyerTypeId) {
      toast.error("Missing parking package configuration.");
      return;
    }
    setParkingSubmitError(null);
    const ticketIds: number[] = [];
    const parkingSelections: TdcSeatSelection[] = [];
    for (const block of parkingBlocks) {
      for (const ticket of block.tickets) {
        if (ticket.seatId) ticketIds.push(ticket.seatId);
        if (ticket.section && ticket.row && ticket.seat) {
          parkingSelections.push({
            section: ticket.section,
            row: ticket.row,
            seat: ticket.seat,
          });
        }
        if (parkingSelections.length >= parkingQuantity) break;
      }
      if (parkingSelections.length >= parkingQuantity) break;
    }
    if (parkingSelections.length < parkingQuantity) {
      toast.error("Not enough parking spaces available.");
      return;
    }
    try {
      const cart = await addParkingSeats({
        tdcPackageListId: tdc.parkingPackageListId,
        tdcBuyerTypeId: tdc.parkingPackageBuyerTypeId,
        ticketIds: ticketIds.slice(0, parkingQuantity),
        tdcPackageId: tdc.packageId,
      });
      if (cart?.expiresAt) setHoldExpiresAt(new Date(cart.expiresAt));
      if (cart?.cartId) setTdcCartId(cart.cartId);
      setConfirmedParkingSelections(parkingSelections);
      const sectionLabel =
        parkingSections.find((s) => s.section === selectedParkingSection)
          ?.sectionDisplay || selectedParkingSection;
      setConfirmedParkingSectionLabel(sectionLabel);
      setParkingDialogVisible(false);
      await refreshFinalCharge(parkingSelections);
    } catch {
      // Inline banner only; the HTTP interceptor already toasts the
      // localized message.
      setParkingSubmitError("Couldn't add parking spaces. Please try again.");
    }
  };

  const refreshFinalCharge = async (parkingSelections: TdcSeatSelection[]) => {
    if (cartSeats.length === 0) return;
    const seatIds = seatIdsOf(cartSeats);
    if (seatIds.length === 0) return;
    const tdcPackageSelections = buildTdcPackageSelections(
      seatSelectionsOf(cartSeats),
      parkingSelections,
    );
    try {
      setChargeError(null);
      const charge = await calculateCharge({
        planPackageId: planPackage.id,
        section: normalizedSection || cartSeats[0]?.section,
        seatIds,
        eventType,
        tdcPackageBuyerTypeId: tdc.packageBuyerTypeId || undefined,
        tdcPackageSelections:
          tdcPackageSelections.length > 0 ? tdcPackageSelections : undefined,
      });
      setFinalCharge(charge);
    } catch {
      // Inline banner only; the HTTP interceptor already toasts the
      // localized message (e.g. TICKET_CANNOT_CALCULATE_CHARGES).
      setChargeError("Failed to calculate charges. Please try again.");
    }
  };

  // ---- Purchase ----
  const seatLabel = (seat: SeatDetails) =>
    `Section ${seat.sectionDisplay || seat.section} · Row ${seat.row} · Seat ${
      seat.seatDisplay || seat.seat
    }`;

  const validateReadyToPurchase = (): string | null => {
    if (cartSeats.length === 0 || seatIdsOf(cartSeats).length === 0)
      return "Missing seat selection. Please reserve seats again.";
    if (!hasRequiredParking)
      return "Please add the required parking selection first.";
    if (!billingInterval) return "Missing billing interval.";
    if (isTdcPlan && !tdcCartId)
      return "Missing ticket cart. Please reserve seats again.";
    return null;
  };

  const needsPhoneVerification = () =>
    !!currentUser &&
    !currentUser.phoneGrandfathered &&
    !currentUser.phoneVerified &&
    tenantConfig?.requirePhoneVerification !== false;

  const completePurchase = async (): Promise<TokenReceivedOutcome> => {
    const validationError = validateReadyToPurchase();
    if (validationError) {
      toast.error(validationError);
      return "failed";
    }
    try {
      setPurchaseError(null);
      await confirmPurchase({
        planPackageId: planPackage.id,
        seatIds: seatIdsOf(cartSeats),
        billingInterval,
        eventType,
        tdcCartId: tdcCartId || undefined,
        tdcPackageListId: tdc.packageListId,
        tdcPackageId: tdc.packageId,
        tdcPackageBuyerTypeId: tdc.packageBuyerTypeId || undefined,
      });
      onComplete({
        productName: product.name,
        variantLabel: variant.label,
        kidsClub: false,
        memberNames: cartSeats.map(seatLabel),
        memberNamesTitle: "Your seats",
        amountLabel: `${formatCurrency(chargeTotal)}`,
      });
      return "completed";
    } catch {
      // Inline banner only; the HTTP interceptor already toasts the
      // localized message (e.g. BILLING_ERROR, TICKET_NOT_FOUND).
      setPurchaseError("Purchase failed. Please try again.");
      return "failed";
    }
  };

  const startPurchase = async (): Promise<TokenReceivedOutcome> => {
    if (needsPhoneVerification()) {
      setPendingConfirm(true);
      setVerifyPhoneVisible(true);
      // The card was saved; the purchase resumes after phone verification.
      return "deferred";
    }
    return completePurchase();
  };

  const handleTokenReceived = async (
    error: StripeTokenError | undefined,
    response?: StripeTokenResponse,
  ): Promise<TokenReceivedOutcome> => {
    if (error) {
      toast.error(error.error.message);
      return "failed";
    }
    if (!response || !account?.id) return "failed";
    try {
      await updateBilling({ accountId: account.id, token: response });
      await fetchAccount();
    } catch {
      // The global HTTP interceptor already toasts the localized message.
      return "failed";
    }
    return startPurchase();
  };

  // Resume the purchase after phone verification completes.
  useEffect(() => {
    if (!pendingConfirm || !currentUser) return;
    if (currentUser.phoneGrandfathered || currentUser.phoneVerified) {
      setPendingConfirm(false);
      completePurchase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, pendingConfirm]);

  const isSectionLoading = normalizedSection
    ? isFetchingBlocks
    : isFetchingSections;

  return (
    <Container>
      <Pressable testID="v3-season-back" onPress={onBack} className="py-4">
        <Text category="s2" style={{ color: primaryColor }}>
          ← Back to {product.name}
        </Text>
      </Pressable>
      <Text category="h5">{product.name}</Text>
      {product.tagline ? (
        <Text category="p2" appearance="hint" style={{ marginBottom: 12 }}>
          {product.tagline}
        </Text>
      ) : null}

      {variants.length > 1 ? (
        <View className="flex-row flex-wrap gap-2 mb-3">
          {variants.map((live) => {
            const isSelected = live.variant.key === selected.variant.key;
            return (
              <Pressable
                key={live.variant.key}
                testID={`v3-season-variant-${live.variant.key}`}
                accessibilityRole="button"
                accessibilityState={{ selected: isSelected }}
                onPress={() => handleVariantChange(live)}
                style={{
                  borderWidth: 2,
                  borderColor: isSelected ? primaryColor : LINE,
                  borderRadius: 10,
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                }}
              >
                <Text category="s2">
                  {live.variant.label}
                  {live.variant.popular ? " ★" : ""}
                </Text>
                {live.variant.tagline ? (
                  <Text category="c1" appearance="hint">
                    {live.variant.tagline}
                  </Text>
                ) : null}
              </Pressable>
            );
          })}
        </View>
      ) : null}

      {planPackage.billingIntervals.length > 1 ? (
        <View className="mb-3">
          <Text category="h6" style={{ marginBottom: 6 }}>
            Payment schedule
          </Text>
          <View className="flex-row gap-6">
            <Radio
              testID="v3-season-interval-monthly"
              checked={billingInterval === "MONTHLY"}
              onChange={() => setBillingInterval("MONTHLY")}
            >
              Payment plan
            </Radio>
            <Radio
              testID="v3-season-interval-full"
              checked={billingInterval === "FULL_SEASON"}
              onChange={() => setBillingInterval("FULL_SEASON")}
            >
              Pay in full
            </Radio>
          </View>
        </View>
      ) : null}

      {/* Seat map + seat picker */}
      <View
        testID="v3-season-seat-map"
        className="border rounded-2xl mb-4"
        style={{ borderColor: LINE, backgroundColor: PAPER, overflow: "hidden" }}
      >
        {canRenderSeatMap ? (
          <View style={{ height: 320 }}>
            <VenueSeatMap
              mapId={`v3-season-seat-map-${variant.key}`}
              tenantType={branding!.type}
              tenantSubdomain={branding!.subdomain!}
              theme={tenantThemeColor}
              eventType={eventType!}
              venueName={venueName!}
              selectedPartySize={1}
              blocks={sections as any}
              seatGroups={planPackage.primaryComponent?.seatGroups || []}
              multipleSectionsAllowed={false}
              includePremium={true}
              onSectionAdded={handleSectionChanged}
              onSectionRemoved={() => handleSectionChanged(undefined)}
              onReset={handleBackToSections}
              preferredSections={normalizedSection ? [normalizedSection] : []}
              venueMapKey={venueMapKey}
            />
          </View>
        ) : (
          <View className="p-4">
            <Text appearance="hint" category="p2">
              Seat map is not configured for this product yet.
            </Text>
          </View>
        )}
        <View className="p-3" style={{ borderTopWidth: 1, borderTopColor: LINE }}>
          {isSectionsError ? (
            <InlineError
              message="Couldn't load available sections."
              actionLabel="Retry"
              onAction={() => refetchSections()}
            />
          ) : null}
          {normalizedSection ? (
            <>
              {isBlocksError ? (
                <InlineError
                  message="Couldn't load seats for this section."
                  actionLabel="Retry"
                  onAction={() => refetchBlocks()}
                />
              ) : null}
              <AvailableSeatDots
                seatsByRow={seatsByRow}
                availableSeatIds={availableSeatIds}
                selectedSeats={selectedSeats}
                primaryColor={primaryColor}
                loading={isSectionLoading || isFetchingSeatsByRow}
                disabled={isCartActive}
                onSelectionChange={setSelectedSeats}
              />
              <View className="mt-3">
                <Button
                  testID="v3-season-reserve"
                  onPress={handleReserve}
                  disabled={
                    isCartActive ||
                    selectedSeats.length === 0 ||
                    isSectionLoading ||
                    isHolding ||
                    isCalculatingCharge
                  }
                  loading={isHolding}
                >
                  Reserve
                </Button>
              </View>
              {holdError ? (
                <InlineError
                  message={holdError}
                  actionLabel="Retry"
                  onAction={() => holdSelectedSeats(selectedSeats)}
                />
              ) : null}
            </>
          ) : (
            <Text appearance="hint" category="c1">
              Select a section on the map to see seats.
            </Text>
          )}
        </View>
      </View>

      {summarySeats.length > 0 ? (
        <View
          className="border rounded-2xl p-4 mb-4"
          style={{ borderColor: LINE, backgroundColor: PAPER }}
        >
          {isCartActive && holdCountdownText ? (
            <View className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <View className="flex-row items-center justify-between gap-3">
                <Text category="c1" className="flex-1">
                  {holdCountdownText}
                </Text>
                <Button
                  appearance="ghost"
                  size="tiny"
                  onPress={handleReleaseTickets}
                  disabled={isDeletingCart || isHolding || isConfirming}
                  loading={isDeletingCart}
                >
                  Clear Cart
                </Button>
              </View>
            </View>
          ) : null}
          {isCartActive && isParkingRequired && !hasRequiredParking ? (
            <InlineError
              message={
                parkingSectionsError ||
                "Parking selection required to continue."
              }
              actionLabel="Select Parking"
              onAction={() => openParkingDialog(true)}
            />
          ) : null}
          {chargeError ? (
            <InlineError
              message={chargeError}
              actionLabel="Retry"
              onAction={() => refreshFinalCharge(confirmedParkingSelections)}
            />
          ) : null}
          <SeasonOrderSummaryV3
            seats={summarySeats}
            charge={summaryCharge}
            isLoading={isSummaryLoading}
            primaryColor={primaryColor}
            parkingSpacesCount={
              confirmedParkingSelections.length > 0
                ? confirmedParkingSelections.length
                : undefined
            }
            parkingSectionLabel={confirmedParkingSectionLabel}
            parkingSelections={confirmedParkingSelections}
          />
        </View>
      ) : null}

      {showPayment ? (
        <View
          className="border rounded-2xl p-4 mb-4"
          style={{ borderColor: LINE, backgroundColor: PAPER }}
        >
          {purchaseError ? <InlineError message={purchaseError} /> : null}
          <Text category="h6" style={{ marginBottom: 8 }}>
            Payment
          </Text>
          {hasPaymentMethodOnFile ? (
            <View>
              <CardOnFileV3 />
              <Button
                testID="v3-season-purchase"
                onPress={startPurchase}
                disabled={!finalCharge || isCalculatingCharge || isConfirming}
                loading={isConfirming}
              >
                {`Purchase ${formatCurrency(chargeTotal)}`}
              </Button>
            </View>
          ) : (
            <StripePaymentForm
              btnText={`Pay ${formatCurrency(chargeTotal)}`}
              onTokenReceived={handleTokenReceived}
              disabled={!finalCharge || isCalculatingCharge || isConfirming}
              loading={isUpdatingBilling || isConfirming}
              walletAmount={finalCharge ? chargeTotal : null}
            />
          )}
        </View>
      ) : null}
      <Spacer margin={12} />

      <Dialog
        visible={loginVisible}
        cancelText="Close"
        onCancel={() => {
          setLoginVisible(false);
          setPendingHoldSeats(null);
        }}
      >
        <ScrollView>
          <LoginOrSignupForm
            initialForm="signup"
            marketplace
            loginTitle="Log in to reserve seats"
            signupTitle="Create an account to reserve seats"
          />
        </ScrollView>
      </Dialog>

      <Dialog
        visible={parkingDialogVisible}
        setVisible={setParkingDialogVisible}
        cancelText="Close"
      >
        <ScrollView>
          <Text category="h6">Select Parking Section</Text>
          <Spacer />
          {parkingSectionsError ? (
            <InlineError
              message={parkingSectionsError}
              actionLabel="Retry"
              onAction={() => openParkingDialog(true)}
            />
          ) : null}
          {parkingSubmitError ? (
            <InlineError message={parkingSubmitError} />
          ) : null}
          {isFetchingParkingSections ? (
            <Text appearance="hint">Loading parking sections...</Text>
          ) : parkingSections.length === 0 ? (
            <Text appearance="hint">No parking sections available.</Text>
          ) : (
            <View className="gap-2">
              {parkingSections.map((section) => {
                const isSelected = section.section === selectedParkingSection;
                return (
                  <Pressable
                    key={section.section}
                    onPress={() => {
                      setSelectedParkingSection(section.section);
                      setParkingQuantity(1);
                      setParkingSubmitError(null);
                    }}
                  >
                    <View
                      className="rounded-lg px-3 py-2"
                      style={{
                        borderWidth: isSelected ? 2 : 1,
                        borderColor: isSelected ? primaryColor : LINE,
                      }}
                    >
                      <Text>{section.sectionDisplay || section.section}</Text>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          )}

          {selectedParkingSection ? (
            <>
              <Spacer />
              <Text category="c1">Select Quantity</Text>
              <Spacer />
              {isParkingBlocksError ? (
                <InlineError
                  message="Couldn't load available spaces."
                  actionLabel="Retry"
                  onAction={() => refetchParkingBlocks()}
                />
              ) : isFetchingParkingBlocks ? (
                <Text appearance="hint">Loading available spaces...</Text>
              ) : maxParkingQuantity === 0 ? (
                <Text appearance="hint">
                  No parking spaces available for this section.
                </Text>
              ) : (
                <>
                  <SeatQuantitySelector
                    size={maxParkingQuantity}
                    selectedPartySize={parkingQuantity}
                    onChange={setParkingQuantity}
                    eventType="PARKING"
                  />
                  <Spacer />
                  <Button
                    onPress={handleSubmitParking}
                    disabled={isAddingParking}
                    loading={isAddingParking}
                  >
                    Submit
                  </Button>
                </>
              )}
            </>
          ) : null}
        </ScrollView>
      </Dialog>

      <VerifyPhonePopup
        visible={verifyPhoneVisible}
        onCancel={() => {
          setVerifyPhoneVisible(false);
          setPendingConfirm(false);
        }}
      />
    </Container>
  );
};

export default SeasonCheckoutV3;
